import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Shield,
  Loader2,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Info,
  Activity,
  Clock,
  FileSearch,
  ChevronRight,
  AlertOctagon,
  EyeOff,
  Layers,
} from 'lucide-react';
import api from '../services/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
import type { ClientSEOSummary, SEOFinding, SEOChangeEvent } from '../services/api';

type SEOSummary = ClientSEOSummary;

interface CrawlRunStatus {
  id: string;
  status: string;
  pages_crawled: number;
  pages_errored: number;
  started_at: string | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const severityColor = (sev: string) => {
  switch (sev) {
    case 'critical':
      return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
    case 'high':
      return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300';
    case 'medium':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
    case 'low':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
    case 'info':
    default:
      return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400';
  }
};

const severityIcon = (sev: string) => {
  switch (sev) {
    case 'critical':
      return <AlertOctagon className="w-3 h-3 shrink-0" />;
    case 'high':
      return <AlertTriangle className="w-3 h-3 shrink-0" />;
    case 'medium':
      return <AlertTriangle className="w-3 h-3 shrink-0" />;
    case 'low':
      return <Info className="w-3 h-3 shrink-0" />;
    case 'info':
    default:
      return <Info className="w-3 h-3 shrink-0" />;
  }
};

const scoreColor = (score: number) => {
  if (score >= 80) return 'text-green-600 dark:text-green-400 border-green-500';
  if (score >= 60) return 'text-amber-600 dark:text-amber-400 border-amber-500';
  return 'text-red-600 dark:text-red-400 border-red-500';
};

const statusBadge = (status: string) => {
  switch (status) {
    case 'completed':
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
    case 'running':
    case 'in_progress':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
    case 'failed':
    case 'error':
      return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
    default:
      return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400';
  }
};

const formatTimestamp = (ts: string) => {
  const d = new Date(ts);
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDuration = (startedAt: string) => {
  const start = new Date(startedAt).getTime();
  const now = Date.now();
  const seconds = Math.round((now - start) / 1000);
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs}s`;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
interface Props {
  clientId: string;
}

const ClientDetailSEOSection = ({ clientId }: Props) => {
  const [summary, setSummary] = useState<SEOSummary | null>(null);
  const [priorities, setPriorities] = useState<SEOFinding[]>([]);
  const [changeEvents, setChangeEvents] = useState<SEOChangeEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [auditRunning, setAuditRunning] = useState(false);
  const [crawlStatus, setCrawlStatus] = useState<CrawlRunStatus | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ---- Data fetching ----
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [summaryData, priorityData, eventsData] = await Promise.all([
        api.getClientSEOSummary(clientId),
        api.getSEOPriorities(clientId),
        api.getSEOChangeEvents(clientId),
      ]);
      setSummary(summaryData);
      setPriorities(priorityData.results.slice(0, 5));
      setChangeEvents(eventsData.results.slice(0, 5));
    } catch (err) {
      console.error('Failed to load SEO section data:', err);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadData();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [loadData]);

  // ---- Run audit / crawl ----
  const handleRunAudit = async () => {
    try {
      setAuditRunning(true);
      const { crawl_run_id } = await api.triggerClientCrawl(clientId);

      // Immediately fetch status once
      const initial = await api.getSEOCrawlRunStatus(crawl_run_id);
      setCrawlStatus(initial);

      // Poll every 3 seconds until completed / failed
      pollRef.current = setInterval(async () => {
        try {
          const status = await api.getSEOCrawlRunStatus(crawl_run_id);
          setCrawlStatus(status);

          if (status.status === 'completed' || status.status === 'failed' || status.status === 'error') {
            if (pollRef.current) clearInterval(pollRef.current);
            pollRef.current = null;
            setAuditRunning(false);
            // Refresh all data after crawl finishes
            loadData();
          }
        } catch {
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = null;
          setAuditRunning(false);
        }
      }, 3000);
    } catch (err) {
      console.error('Failed to trigger crawl:', err);
      setAuditRunning(false);
    }
  };

  // ---- Finding actions ----
  const handleResolve = async (findingId: string) => {
    try {
      await api.resolveSEOFinding(findingId);
      setPriorities((prev) => prev.filter((f) => f.id !== findingId));
      // Refresh summary counts
      const updated = await api.getClientSEOSummary(clientId);
      setSummary(updated);
    } catch (err) {
      console.error('Failed to resolve finding:', err);
    }
  };

  const handleSuppress = async (findingId: string) => {
    try {
      await api.suppressSEOFinding(findingId);
      setPriorities((prev) => prev.filter((f) => f.id !== findingId));
      const updated = await api.getClientSEOSummary(clientId);
      setSummary(updated);
    } catch (err) {
      console.error('Failed to suppress finding:', err);
    }
  };

  // ---- Loading state ----
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="panel p-3 text-center">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Unable to load SEO health data.
        </p>
      </div>
    );
  }

  const { finding_counts: fc } = summary;

  return (
    <div className="space-y-2">
      {/* ------------------------------------------------------------------ */}
      {/* ROW 1: SEO Health Score + Latest Crawl Status                      */}
      {/* ------------------------------------------------------------------ */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {/* SEO Health Card */}
        <div className="panel p-3">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="w-4 h-4 text-purple-500" />
            <h3 className="text-xs font-semibold text-gray-900 dark:text-white">SEO Health</h3>
          </div>
          <div className="flex items-center gap-4">
            {/* Score badge */}
            <div
              className={`flex items-center justify-center w-14 h-14 rounded-full border-2 text-lg font-bold ${scoreColor(summary.audit_score)}`}
            >
              {summary.audit_score}
            </div>

            <div className="flex-1 space-y-1.5">
              <div className="flex justify-between text-xs">
                <span className="text-gray-500 dark:text-gray-400">Total Findings</span>
                <span className="font-medium text-gray-900 dark:text-white">{fc.total}</span>
              </div>
              <div className="flex flex-wrap gap-1">
                {fc.critical > 0 && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
                    {fc.critical} critical
                  </span>
                )}
                {fc.high > 0 && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300">
                    {fc.high} high
                  </span>
                )}
                {fc.medium > 0 && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                    {fc.medium} medium
                  </span>
                )}
                {fc.low > 0 && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                    {fc.low} low
                  </span>
                )}
                {fc.info > 0 && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400">
                    {fc.info} info
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Latest Crawl Status */}
        <div className="panel p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <FileSearch className="w-4 h-4 text-blue-500" />
              <h3 className="text-xs font-semibold text-gray-900 dark:text-white">Latest Crawl</h3>
            </div>
            {/* Run Audit Button */}
            <button
              onClick={handleRunAudit}
              disabled={auditRunning}
              className="btn btn-primary text-xs"
            >
              {auditRunning ? (
                <>
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Running...
                </>
              ) : (
                <>
                  <RefreshCw className="w-3 h-3" />
                  Run Audit
                </>
              )}
            </button>
          </div>

          {/* Active crawl progress */}
          {auditRunning && crawlStatus && (
            <div className="mb-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded text-xs text-blue-700 dark:text-blue-300 flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin shrink-0" />
              <span>
                Crawling... {crawlStatus.pages_crawled} pages scanned
                {crawlStatus.pages_errored > 0 && ` (${crawlStatus.pages_errored} errors)`}
              </span>
            </div>
          )}

          {summary.latest_crawl ? (
            <div className="space-y-1.5 text-xs">
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Status</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${statusBadge(summary.latest_crawl.status)}`}>
                  {summary.latest_crawl.status}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Pages Crawled</span>
                <span className="font-medium text-gray-900 dark:text-white">{summary.latest_crawl.pages_crawled.toLocaleString()}</span>
              </div>
              {summary.latest_crawl.pages_errored > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">Errors</span>
                  <span className="font-medium text-red-600 dark:text-red-400">{summary.latest_crawl.pages_errored}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Duration</span>
                <span className="font-medium text-gray-900 dark:text-white">{formatDuration(summary.latest_crawl.started_at || '')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Started</span>
                <span className="font-medium text-gray-900 dark:text-white">{formatTimestamp(summary.latest_crawl.started_at || '')}</span>
              </div>
            </div>
          ) : (
            <p className="text-xs text-gray-500 dark:text-gray-400 text-center py-3">
              No crawls recorded yet. Click "Run Audit" to start.
            </p>
          )}
        </div>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* ROW 2: Top 5 Priorities                                            */}
      {/* ------------------------------------------------------------------ */}
      <div className="panel p-3">
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle className="w-4 h-4 text-amber-500" />
          <h3 className="text-xs font-semibold text-gray-900 dark:text-white">Top 5 Priorities</h3>
        </div>

        {priorities.length === 0 ? (
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center py-3">
            No priority findings. Great job!
          </p>
        ) : (
          <div className="space-y-1.5">
            {priorities.map((finding) => (
              <div
                key={finding.id}
                className="flex items-start gap-2 p-2 bg-gray-50 dark:bg-gray-800 rounded text-xs"
              >
                <span className={`flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-medium shrink-0 mt-0.5 ${severityColor(finding.severity)}`}>
                  {severityIcon(finding.severity)}
                  {finding.severity}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 dark:text-white truncate">{finding.rule_id}</p>
                  <p className="text-gray-500 dark:text-gray-400 truncate">{finding.observation}</p>
                  {finding.page_url && (
                    <p className="text-gray-400 dark:text-gray-500 truncate mt-0.5">{finding.page_url}</p>
                  )}
                </div>
                <div className="flex gap-1 shrink-0">
                  <button
                    onClick={() => handleResolve(finding.id)}
                    className="p-1 hover:bg-green-100 dark:hover:bg-green-900/30 rounded transition-colors"
                    title="Resolve"
                  >
                    <CheckCircle className="w-3.5 h-3.5 text-green-600 dark:text-green-400" />
                  </button>
                  <button
                    onClick={() => handleSuppress(finding.id)}
                    className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                    title="Suppress"
                  >
                    <EyeOff className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* ROW 3: Recent Changes + Coverage Gaps                              */}
      {/* ------------------------------------------------------------------ */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {/* Recent Changes */}
        <div className="panel p-3">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="w-4 h-4 text-indigo-500" />
            <h3 className="text-xs font-semibold text-gray-900 dark:text-white">Recent Changes</h3>
          </div>

          {changeEvents.length === 0 ? (
            <p className="text-xs text-gray-500 dark:text-gray-400 text-center py-3">
              No recent change events detected.
            </p>
          ) : (
            <div className="space-y-1.5">
              {changeEvents.map((event) => (
                <div
                  key={event.id}
                  className="flex items-start gap-2 text-xs"
                >
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium shrink-0 mt-0.5 ${severityColor(event.severity)}`}>
                    {event.severity}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-900 dark:text-white truncate">{event.change_type}</p>
                    <div className="flex items-center gap-1 text-gray-400 dark:text-gray-500 mt-0.5">
                      <Clock className="w-2.5 h-2.5" />
                      <span>{formatTimestamp(event.created_at)}</span>
                    </div>
                  </div>
                  <ChevronRight className="w-3 h-3 text-gray-400 shrink-0 mt-0.5" />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Coverage Gaps */}
        <div className="panel p-3">
          <div className="flex items-center gap-2 mb-2">
            <Layers className="w-4 h-4 text-rose-500" />
            <h3 className="text-xs font-semibold text-gray-900 dark:text-white">Coverage Gaps</h3>
          </div>

          {summary.coverage_gap_count === 0 ? (
            <div className="flex flex-col items-center justify-center py-4">
              <CheckCircle className="w-6 h-6 text-green-500 mb-1" />
              <p className="text-xs text-gray-500 dark:text-gray-400">Full coverage -- no gaps detected.</p>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500 dark:text-gray-400">Total Gaps</span>
                <span className="text-sm font-semibold text-gray-900 dark:text-white">{summary.coverage_gap_count}</span>
              </div>
              {/* Visual bar breakdown by severity */}
              <div className="w-full h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden flex">
                {fc.critical > 0 && (
                  <div
                    className="h-full bg-red-500"
                    style={{ width: `${(fc.critical / fc.total) * 100}%` }}
                    title={`${fc.critical} critical`}
                  />
                )}
                {fc.high > 0 && (
                  <div
                    className="h-full bg-orange-500"
                    style={{ width: `${(fc.high / fc.total) * 100}%` }}
                    title={`${fc.high} high`}
                  />
                )}
                {fc.medium > 0 && (
                  <div
                    className="h-full bg-amber-500"
                    style={{ width: `${(fc.medium / fc.total) * 100}%` }}
                    title={`${fc.medium} medium`}
                  />
                )}
                {fc.low > 0 && (
                  <div
                    className="h-full bg-blue-400"
                    style={{ width: `${(fc.low / fc.total) * 100}%` }}
                    title={`${fc.low} low`}
                  />
                )}
                {fc.info > 0 && (
                  <div
                    className="h-full bg-gray-400"
                    style={{ width: `${(fc.info / fc.total) * 100}%` }}
                    title={`${fc.info} info`}
                  />
                )}
              </div>
              {/* Legend */}
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px]">
                {fc.critical > 0 && (
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-red-500 inline-block" />
                    Critical ({fc.critical})
                  </span>
                )}
                {fc.high > 0 && (
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-orange-500 inline-block" />
                    High ({fc.high})
                  </span>
                )}
                {fc.medium > 0 && (
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-amber-500 inline-block" />
                    Medium ({fc.medium})
                  </span>
                )}
                {fc.low > 0 && (
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-blue-400 inline-block" />
                    Low ({fc.low})
                  </span>
                )}
                {fc.info > 0 && (
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-gray-400 inline-block" />
                    Info ({fc.info})
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientDetailSEOSection;
